/* ↗ .cursor/references/code-notes/r-img.js.md#§1 */

const LOADED_ATTR = 'data-r-loaded';
const PENDING_ATTR = 'data-r-pending';
const SELECTOR = 'img.r-img';
const REVEAL_CLASS = 'r-img--reveal';

/* ↗ .cursor/references/code-notes/r-img.js.md#§2 */
const STALL_MS = 2500;
const MAX_RETRIES = 2;
/* ↗ .cursor/references/code-notes/r-img.js.md#§3 */
const STALL_ROOT_MARGIN = '300px 0px';

/** @type {WeakMap<Element, { tries: number, timer: ReturnType<typeof setTimeout> | undefined }>} */
const watched = new WeakMap();

/** @type {IntersectionObserver | undefined} */
let stallObserver;

/** @param {Element} img */
function unwatch(img) {
  const state = watched.get(img);
  if (state?.timer) clearTimeout(state.timer);
  watched.delete(img);
  stallObserver?.unobserve(img);
}

/* ↗ .cursor/references/code-notes/r-img.js.md#§4 */
/**
 * @param {HTMLImageElement} img
 * @param {boolean} animated
 */
function markLoaded(img, animated) {
  img.removeAttribute(PENDING_ATTR);
  if (!img.hasAttribute(LOADED_ATTR)) img.setAttribute(LOADED_ATTR, animated ? '' : 'ready');
  unwatch(img);
}

/** @param {Event} event */
function onSettled(event) {
  const img = /** @type {HTMLImageElement} */ (event.currentTarget);
  img.removeEventListener('load', onSettled);
  img.removeEventListener('error', onSettled);
  markLoaded(img, true);
}

/** @param {HTMLImageElement} img */
function listen(img) {
  img.addEventListener('load', onSettled);
  img.addEventListener('error', onSettled);
}

/**
 * Re-issues a hung request. Nothing is cleared first: blanking src/srcset makes
 * the element fire `error`, and the error listener would mark it loaded — the
 * opposite of the intent. Dropping the lazy deferral takes it out of the queue;
 * re-assigning `srcset` (or `src`) re-runs source selection, which is what
 * actually aborts the hung fetch and starts a new one.
 *
 * fetchpriority stays untouched until the LAST attempt. A network blip can
 * stall a whole screenful at once, and a herd of high-priority retries would
 * compete with whatever the page legitimately wants next; one final
 * escalation per image is enough.
 * @param {HTMLImageElement} img
 * @param {number} attempt
 */
function restart(img, attempt) {
  img.loading = 'eager';
  if (attempt >= MAX_RETRIES) img.fetchPriority = 'high';
  if (img.srcset) img.srcset = img.srcset;
  else if (img.src) img.src = img.src;
}

/** @param {HTMLImageElement} img */
function checkStalled(img) {
  if (!img.isConnected || img.complete) {
    unwatch(img);
    return;
  }

  const state = watched.get(img);
  if (!state) return;

  if (state.tries >= MAX_RETRIES) {
    unwatch(img);
    return;
  }

  state.tries += 1;
  restart(img, state.tries);
  state.timer = setTimeout(() => checkStalled(img), STALL_MS * (state.tries + 1));
}

/** @param {HTMLImageElement} img */
function stall(img) {
  if (!('IntersectionObserver' in window)) return;
  stallObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const img = /** @type {HTMLImageElement} */ (entry.target);

        if (img.complete) {
          unwatch(img);
          continue;
        }

        if (!entry.isIntersecting) {
          // Out of reach again: stop the clock rather than retrying something
          // nobody is looking at. Tries are kept; the clock re-arms on re-entry.
          const state = watched.get(img);
          if (state?.timer) clearTimeout(state.timer);
          if (state) state.timer = undefined;
          continue;
        }

        const state = watched.get(img) ?? { tries: 0, timer: undefined };
        if (state.timer) continue;
        state.timer = setTimeout(() => checkStalled(img), STALL_MS);
        watched.set(img, state);
      }
    },
    { rootMargin: STALL_ROOT_MARGIN }
  );
  stallObserver.observe(img);
}

/* ↗ .cursor/references/code-notes/r-img.js.md#§5 */
/** @param {HTMLImageElement} img */
function absorbOne(img) {
  if (img.complete) {
    markLoaded(img, false);
    return;
  }
  listen(img);
  if (img.naturalWidth === 0 && img.classList.contains(REVEAL_CLASS) && !img.hasAttribute(LOADED_ATTR)) {
    img.setAttribute(PENDING_ATTR, '');
  }
  stall(img);
}

/** @param {ParentNode} [root] */
function absorb(root = document.body) {
  for (const img of root.querySelectorAll(SELECTOR)) absorbOne(/** @type {HTMLImageElement} */ (img));
}

absorb();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => absorb(), { once: true });
}
document.addEventListener('shopify:section:load', (event) => {
  absorb(event.target instanceof Element ? event.target : undefined);
});

/* ↗ .cursor/references/code-notes/r-img.js.md#§6 */
if ('MutationObserver' in window) {
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') {
        const el = record.target;
        if (!(el instanceof HTMLImageElement) || !el.classList.contains('r-img')) continue;
        if (record.attributeName === LOADED_ATTR) {
          if (!el.hasAttribute(LOADED_ATTR) && (el.complete || el.naturalWidth > 0)) {
            markLoaded(el, false);
            if (!el.complete) stall(el);
          }
        } else if (!el.complete) {
          listen(el);
          stall(el);
        }
        continue;
      }

      for (const node of record.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = /** @type {Element} */ (node);
        if (el instanceof HTMLImageElement) {
          if (el.classList.contains('r-img')) absorbOne(el);
        } else if (el.firstElementChild) {
          absorb(el);
        }
      }
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', LOADED_ATTR],
  });
}
